<template>
  <!-- The public home: the counter. The artwork is the best thing here, so it
       goes big; the interface keeps a straight face. Every price and promise
       on this page is one the backend keeps (checked line by line, S15) — when
       one stops being true, change the page, not the wording around it. -->
  <main class="page home">

    <p class="lab red">Postcards &middot; letters &middot; prints &middot; books &middot; by post</p>
    <h1 class="d1 head">Put something unreasonable in the post.</h1>
    <div class="two bottom">
      <p class="deck">A 4 &times; 6 card with artwork that exists for one person and gets printed exactly once. It turns up on a
        Tuesday, for no occasion, and ruins their composure a bit. That is the entire product.</p>
      <div>
        <p class="sm narrow"><b class="money">$5.00</b> for one card, printed and posted. No previews and no edits: it is a
          surprise to you as well, which is most of the fun.</p>
        <p class="sm narrow gap"><b class="money">$12</b> a month if you&#8217;d rather something kept an eye on the calendar for
          you. The cards it sends are included. A gift is priced per item and charged only on your yes.</p>
        <div class="btnrow gap-l">
          <router-link to="/create-postcard" class="btn">Send one &mdash; $5.00</router-link>
          <router-link to="/membership" class="btn ghost">Keep a desk &mdash; $12/month</router-link>
        </div>
      </div>
    </div>

    <hr class="rulehr">

    <!-- the proof: real briefs, real output, nothing retouched -->
    <div class="kick"><h2 class="d2">A sentence went in. This came out.</h2><span class="lab">Two real briefs &middot; made once each</span></div>

    <div class="two even">
      <figure class="obj">
        <div class="pc pc-front"><img :src="art.potato" width="800" height="800"
          alt="Coloured-pencil drawing on a sticky note: a smiling potato in a red headband, flexing and running over the Chicago skyline."></div>
        <figcaption class="cap"><b>For a brother in Chicago.</b> The brief, in full, all fourteen words:
          <em>&#8220;my brother just finished his first marathon in Chicago and is limping around proudly.&#8221;</em>
          Nobody asked for a potato. A potato was the correct answer.</figcaption>
      </figure>
      <div>
        <figure class="obj">
          <div class="pc pc-front"><img :src="art.sourdough" width="800" height="800"
            alt="Watercolour and ink drawing of a jar of sourdough starter with a smiling face, a wooden spoon leaning on it."></div>
        </figure>
        <figure class="obj flipside">
          <div class="pc pc-back">
            <div class="pc-msg">
              <p>Dear Mom,</p>
              <p>Congratulations on becoming the proud parent of a living, breathing sourdough starter! Here&#8217;s to many more
                delicious adventures and bubbly kitchen experiments. You&#8217;ve got this kneaded down! &#127838;</p>
              <p class="sign">Love always, Me</p>
            </div>
            <div class="pc-right">
              <svg class="stamp" viewBox="0 0 88 104" aria-hidden="true">
                <rect x="4" y="4" width="80" height="96" fill="#FDEFF6"/>
                <rect x="10" y="12" width="68" height="60" fill="#FFE6F2"/>
                <path d="M10 54 L28 34 L44 52 L58 32 L78 58 L78 72 L10 72Z" fill="#DFA0C6"/>
                <circle cx="62" cy="26" r="9" fill="#FF2E88"/>
                <text x="44" y="86" text-anchor="middle" font-family="ui-monospace,Menlo,monospace" font-size="9" letter-spacing="1.4" fill="#3A3344">FIRST CLASS</text>
                <text x="44" y="96" text-anchor="middle" font-family="ui-monospace,Menlo,monospace" font-size="7.5" letter-spacing="1" fill="#3A3344" opacity=".7">THINKING OF U</text>
                <rect x="4" y="4" width="80" height="96" fill="none" stroke="#DFA0C6" stroke-width="1.5" stroke-dasharray="3 3"/>
              </svg>
              <address class="pc-addr">MOM<br>HER STREET<br>HER TOWN</address>
            </div>
            <span class="pc-mark">Thinking of U</span>
          </div>
        </figure>
        <p class="cap"><b>For a mother, both sides.</b> Brief: <em>&#8220;my mom finally kept a sourdough starter alive for a whole
          month.&#8221;</em> The back is printed exactly as it was written, pun included. We had no say in the pun.</p>
      </div>
    </div>

    <div class="panel kraftp drawn">
      <span class="rstamp live">Drawn here</span>
      <p class="sm">Each of those was made once, from that sentence, by the same pipeline your card goes through &mdash; no
        retouching, no picking the best of ten. Ask it the potato&#8217;s sentence again and you get something else entirely
        (this week: a penguin with a megaphone), which is the point: it doesn&#8217;t make the same card twice. In fairness, we
        left one out. Asked for a three-legged greyhound, it drew four legs.</p>
    </div>

    <hr class="rulehr">

    <!-- the catalogue -->
    <div class="kick"><h2 class="d2">The catalogue</h2><span class="lab">Five things &middot; four of them open</span></div>

    <div class="grid3">
      <article>
        <figure class="obj">
          <div class="pc pc-back">
            <div class="pc-msg">
              <p>Maya &mdash; word is the trails have you back, which finally explains why you never pick up.</p>
              <p>No reply needed, no news here.</p>
              <p class="sign">&mdash; Ian</p>
            </div>
            <div class="pc-right">
              <svg class="stamp" viewBox="0 0 88 104" aria-hidden="true">
                <rect x="4" y="4" width="80" height="96" fill="#FDEFF6"/>
                <rect x="10" y="12" width="68" height="60" fill="#E6EDFB"/>
                <path d="M10 54 L28 34 L44 52 L58 32 L78 58 L78 72 L10 72Z" fill="#0A57C9" opacity=".55"/>
                <circle cx="62" cy="26" r="9" fill="#FF2E88"/>
                <text x="44" y="86" text-anchor="middle" font-family="ui-monospace,Menlo,monospace" font-size="9" letter-spacing="1.4" fill="#3A3344">FIRST CLASS</text>
                <rect x="4" y="4" width="80" height="96" fill="none" stroke="#DFA0C6" stroke-width="1.5" stroke-dasharray="3 3"/>
              </svg>
              <address class="pc-addr">MAYA<br>HER STREET<br>DENVER CO</address>
            </div>
            <span class="pc-mark">Thinking of U</span>
          </div>
        </figure>
        <p class="cap"><b>No. 01 &mdash; Postcard.</b> Artwork on one side, words on the other, and no envelope, so the postman reads
          it too. That&#8217;s the deal with postcards.</p>
        <dl class="slip">
          <div><dt>Trim</dt><dd>4 &times; 6 in</dd></div>
          <div><dt>Window</dt><dd>5&ndash;7 business days in the US</dd></div>
          <div><dt>Price</dt><dd><b class="money">$5.00</b> posted &middot; included for members</dd></div>
        </dl>
      </article>

      <article>
        <figure class="obj">
          <div class="mail">
            <div class="letter-sheet">
              <p class="from">Ian &middot; Portland OR</p>
              <p>Dad &mdash; you said on Sunday that you didn&#8217;t need anything, which I&#8217;ve chosen to read as a dare.</p>
              <p>No parcel, then. Just the rest of the story about the fence, in writing, where you can&#8217;t interrupt it&hellip;</p>
            </div>
            <div class="envelope">
              <div class="flap"></div>
              <div class="env-to">ROBERT<br>HIS STREET<br>SACRAMENTO CA</div>
            </div>
          </div>
        </figure>
        <p class="cap"><b>No. 02 &mdash; Printed letter.</b> One sheet, folded in three, in an envelope. For when a postcard is too
          public and the thing you&#8217;ve got to say runs long.</p>
        <dl class="slip">
          <div><dt>Format</dt><dd>One sheet &middot; #10 envelope</dd></div>
          <div><dt>Window</dt><dd>5&ndash;7 business days</dd></div>
          <div><dt>Price</dt><dd>included for members</dd></div>
        </dl>
      </article>

      <article>
        <figure class="obj">
          <div class="frame"><div class="mat">
            <img :src="art.greyhound" width="800" height="800" loading="lazy"
              alt="Three-colour risograph-style print of a greyhound at full stretch among daisies.">
          </div></div>
        </figure>
        <p class="cap"><b>No. 03 &mdash; A print, made for them.</b> An original drawn for one person, printed large on fine-art
          paper, framed if you like, where it will still be three years after you&#8217;ve forgotten sending it.</p>
        <dl class="slip">
          <div><dt>Made by</dt><dd>Prodigi, from artwork made here</dd></div>
          <div><dt>Sizes</dt><dd>11 &times; 14 print &middot; 16 &times; 20 framed</dd></div>
          <div><dt>Price</dt><dd><b class="money">$35.00</b> &middot; <b class="money">$95.00</b> framed &middot; on your yes</dd></div>
        </dl>
      </article>

      <article>
        <figure class="obj bookwrap">
          <div class="book">
            <div class="spine"><span>The Year of the Starter</span></div>
            <div class="cover">
              <span class="btop">Thinking of U</span>
              <img :src="art.sourdough" alt="" loading="lazy">
              <span class="btitle">The year of the starter</span>
              <span class="bsub">For Mom &middot; 20 pages</span>
            </div>
            <div class="edge"></div>
          </div>
        </figure>
        <p class="cap"><b>No. 04 &mdash; A book.</b> Twenty illustrated pages about one person and one year of their life. Their
          name on the cover.</p>
        <dl class="slip">
          <div><dt>Format</dt><dd>A5 softcover &middot; 20 pages</dd></div>
          <div><dt>Price</dt><dd><b class="money">$39.00</b> &middot; on your yes</dd></div>
        </dl>
      </article>

      <article>
        <figure class="obj">
          <div class="notecard"><span class="rstamp shut">Press not open</span></div>
        </figure>
        <p class="cap"><b>No. 05 &mdash; Handwritten note.</b> Real pen, real pressure, real paper. The press for it is built and
          not switched on, so this drawer stays shut. It is listed because it is coming, not because you can have it.</p>
        <dl class="slip">
          <div><dt>Status</dt><dd>Not taking orders</dd></div>
        </dl>
      </article>

      <article>
        <div class="panel sober order">
          <span class="lab">The standing order</span>
          <h3 class="d3 order-head">$12 a month, and a desk that keeps count so you don&#8217;t have to.</h3>
          <p class="sm">Every morning it reads the people you&#8217;ve added &mdash; what&#8217;s coming up, how long it&#8217;s been,
            whatever you&#8217;ve told it lately &mdash; and decides whether anyone is due. Most days nobody is, and it says so.</p>
          <p class="sm">The cards, notes and letters it sends are included. The print and the book are gifts: it proposes, you
            approve, and only then are you charged &mdash; the standing order does not quietly include a $95 frame.</p>
          <div class="btnrow push">
            <router-link to="/membership" class="btn">Keep a desk &mdash; $12/month</router-link>
          </div>
        </div>
      </article>
    </div>

    <hr class="rulehr">

    <!-- house rules + the tariff -->
    <div class="two">
      <div>
        <div class="kick"><h2 class="d2">House rules</h2><span class="lab">Pinned above the counter</span></div>
        <ul class="ev">
          <li><span><b>Nothing that costs you money happens without your yes.</b> A gift is proposed, priced, and waits. No trial
            runs on your card.</span><span class="meta">Rule 1</span></li>
          <li><span><b>Illness, money trouble and grief are never a reason to sell you something.</b> The desk is told so in as
            many words. They can be a reason to write. They are never a reason to buy.</span><span class="meta">Rule 2</span></li>
          <li><span><b>Their rules outrank yours.</b> Anyone the desk reaches by email gets a link to a page of their own: what
            they&#8217;ll accept, how much in a month across everyone who sends, quiet dates, and where it goes.</span><span class="meta">Rule 3</span></li>
          <li><span><b>Their no is checked on our side, every time.</b> Not argued with, not routed around, not &#8220;are you
            sure&#8221;. Your assistant is told their rules; you aren&#8217;t.</span><span class="meta">Rule 4</span></li>
        </ul>

        <div id="got-one" class="panel got">
          <span class="lab blue">Got something from us?</span>
          <p class="sm">Someone thought of you and we did the posting. If it reached you by email, the link at the bottom opens a
            page that&#8217;s yours: say what you&#8217;d welcome, how often, or nothing at all. It takes a minute and you don&#8217;t
            need an account. If it came on paper, that&#8217;s the whole of it &mdash; enjoy it.</p>
        </div>
      </div>

      <div class="stack-s">
        <div class="panel sober">
          <span class="lab">The tariff</span>
          <table class="tariff">
            <caption class="sr">Prices</caption>
            <tbody>
              <tr><th scope="row">One postcard, no account</th><td class="money">$5.00</td></tr>
              <tr><th scope="row">The standing order, per month</th><td class="money">$12.00</td></tr>
              <tr><th scope="row">Cards, notes and letters it sends</th><td class="mut">included</td></tr>
              <tr><th scope="row">A found gift</th><td class="mut">the listing price</td></tr>
              <tr><th scope="row">A print made for them</th><td class="money">$35.00</td></tr>
              <tr><th scope="row">The same, framed</th><td class="money">$95.00</td></tr>
              <tr><th scope="row">A 20-page book</th><td class="money">$39.00</td></tr>
              <tr><th scope="row">Handwritten note</th><td class="mut">not open</td></tr>
            </tbody>
          </table>
          <p class="xs tail">The standing order buys the attention: someone keeping up with your people, drafting the thing, and
            asking before anything is bought. Gifts are charged only once you say yes. Your first single postcard is half off
            with <b>FIRSTPURCHASE</b>.</p>
        </div>
        <div class="panel kraftp">
          <span class="lab red">Being straight with you</span>
          <p class="sm tail">The desk&#8217;s new brain is on probation. It reads, plans and drafts every morning and holds what it
            would have sent where you can see it; we switch it on, not you, once its judgement holds up. Until then the routine
            cards go out the older, simpler way.</p>
        </div>
      </div>
    </div>

    <hr class="rulehr">

    <!-- the trade counter: other people's assistants -->
    <div class="two">
      <div>
        <span class="lab blue">The trade counter</span>
        <h2 class="d2 trade-head">Other people&#8217;s assistants can post through us. They still can&#8217;t see your street.</h2>
        <p class="lede">A shopping assistant can order a made-for-them postcard here, with a sentence about the person instead of
          a design. Whichever way it comes in, the recipient&#8217;s own rules are checked on our side before anything is made. If
          it asks first, it gets a one-time delivery token, good for a week, and the address goes on the label at our end &mdash;
          never handed back.</p>
      </div>
      <div class="panel sober">
        <span class="lab">What another assistant pays</span>
        <table class="tariff">
          <caption class="sr">Agent-facing catalog prices</caption>
          <tbody>
            <tr><th scope="row">Postcard</th><td class="money">$6.00</td></tr>
            <tr><th scope="row">Printed letter</th><td class="money">$1.80</td></tr>
            <tr><th scope="row">A print made for them</th><td class="money">from $54.00</td></tr>
            <tr><th scope="row">Book, 20pp</th><td class="money">$56.40</td></tr>
          </tbody>
        </table>
        <p class="xs tail">Shipping included. Published openly and machine-readable (<a href="/llms.txt">llms.txt</a>), same price for
          every assistant. A channel with secret rates isn&#8217;t a channel, it&#8217;s a market.</p>
      </div>
    </div>

    <hr class="rulehr">

    <div class="panel airmail closer">
      <h2 class="d2">Start with one person.</h2>
      <p class="lede">Send them a postcard for $5. Or keep a desk for $12 a month and something keeps up with them for you, cards
        included.</p>
      <div class="btnrow center">
        <router-link to="/create-postcard" class="btn">Send one postcard &mdash; $5.00</router-link>
        <router-link to="/membership" class="btn ghost">Keep a desk &mdash; $12/month</router-link>
      </div>
    </div>

    <p class="xs colophon">Colophon: messages are written by a language model (OpenAI&#8217;s GPT-4o) from what you tell us, and
      the artwork is generated for one named person by Google&#8217;s Gemini image model. The desk&#8217;s judgement &mdash; who,
      when, whether at all &mdash; is Anthropic&#8217;s Claude. Postcards and letters are printed and posted by Stannp in the
      United States; prints and books by Prodigi. That disclosure lives down here rather than on the tin, because what you are
      buying is a card, not a model.</p>
  </main>
</template>

<script>
import { useHead } from '@vueuse/head';

const SITE_URL = 'https://thinking-of-u.com';
// The postcard as a product, for shopping surfaces that read structured data
// (Google Shopping / Gemini free listings). Price = the on-page price; agents
// buying through the gateway see the margined ACP/UCP feed price instead.
const POSTCARD_PRODUCT = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  '@id': `${SITE_URL}/#postcard`,
  name: 'Made-for-them postcard',
  description: 'A real 4x6 postcard, printed and mailed anywhere in the US. You pick who it is for and the vibe; the message and artwork are made from scratch for them. No previews, no edits — a little surprise in their mailbox.',
  image: [`${SITE_URL}/og-image.png`],
  brand: { '@type': 'Brand', name: 'Thinking of U' },
  sku: 'postcard',
  category: 'Greeting & Note Cards',
  offers: {
    '@type': 'Offer',
    url: `${SITE_URL}/create-postcard`,
    priceCurrency: 'USD',
    price: '5.00',
    availability: 'https://schema.org/InStock',
    itemCondition: 'https://schema.org/NewCondition',
    shippingDetails: {
      '@type': 'OfferShippingDetails',
      shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'USD' },
      shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'US' },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        transitTime: { '@type': 'QuantitativeValue', minValue: 5, maxValue: 7, unitCode: 'DAY' },
      },
    },
  },
};

export default {
  name: 'MainPage',
  setup() {
    useHead({ script: [{ type: 'application/ld+json', key: 'postcard-product', children: JSON.stringify(POSTCARD_PRODUCT) }] });
  },
  data() {
    return {
      // Real output of the card pipeline, unretouched. potato: the made-for-them
      // probe of 2026-09-21 (b3db60e); sourdough + greyhound: run 2026-10-23
      // through generateMessage → generateImagePrompt → generateImage.
      art: {
        potato: require('../assets/images/proof/potato.webp'),
        sourdough: require('../assets/images/proof/sourdough.webp'),
        greyhound: require('../assets/images/proof/greyhound-riso.webp'),
      },
    };
  },
};
</script>

<style scoped>
.home { width: 100%; box-sizing: border-box; }
.head { margin: 14px 0 20px; max-width: 14ch; }
.two { display: grid; gap: clamp(22px, 4vw, 48px); }
@media (min-width: 860px) {
  .two { grid-template-columns: 1.15fr 1fr; }
  .two.even { grid-template-columns: 1fr 1fr; }
  .two.bottom { align-items: end; }
}
.deck { font-size: clamp(1.04rem, 1.8vw, 1.28rem); line-height: 1.5; color: var(--ink-2); max-width: 40ch; margin: 0; }
.narrow { max-width: 42ch; }
.gap { margin-top: 10px; }
.gap-l { margin-top: 16px; }
.center { justify-content: center; margin-top: 20px; }

.kick { display: flex; align-items: baseline; gap: 12px; margin: 0 0 18px; flex-wrap: wrap; }
.kick::after { content: ""; flex: 1 1 40px; border-top: 1px solid var(--rule); }
.rulehr { border: 0; border-top: 1px solid var(--rule); margin: clamp(34px, 5vw, 60px) 0; }
.stack-s > * + * { margin-top: 12px; }

/* ── the objects, at real proportions ── */
.obj { container-type: inline-size; margin: 0; position: relative; }
.flipside { margin-top: 14px; }
.pc {
  aspect-ratio: 3 / 2; position: relative; overflow: hidden; background: var(--sheet);
  box-shadow: 0 1px 0 rgba(0, 0, 0, .16), 0 16px 30px -20px rgba(40, 26, 10, .6), 0 2px 5px -2px rgba(40, 26, 10, .24);
}
.pc-front img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
.pc-front::after { content: ""; position: absolute; inset: 0; box-shadow: inset 0 0 0 1px rgba(26, 22, 17, .22); }
.pc-back { display: grid; grid-template-columns: 1fr .82fr; padding: 5.2cqi; box-sizing: border-box; }
.pc-back::before { content: ""; position: absolute; left: 54%; top: 9%; bottom: 9%; width: 1px; background: var(--rule); }
.pc-msg { padding: 0 4.6cqi 6cqi 0; }
.pc-msg p { font-family: var(--display); color: var(--blue); font-size: 3.2cqi; line-height: 1.4; margin: 0 0 .4em; }
.pc-msg .sign { font-family: var(--hand); font-size: 5cqi; margin-top: .1em; }
.pc-right { position: relative; display: flex; flex-direction: column; align-items: flex-end; }
.pc-right .stamp { width: 15cqi; height: auto; }
.pc-addr {
  font-family: var(--mono); font-style: normal; font-size: 2.5cqi; line-height: 1.9; color: var(--ink-2);
  margin-top: auto; align-self: stretch; padding-left: 3cqi;
  background: repeating-linear-gradient(to bottom, transparent 0 calc(1.9em - 1px), var(--rule) calc(1.9em - 1px) 1.9em);
  background-position: 0 .3em;
}
.pc-mark {
  position: absolute; left: 5.2cqi; bottom: 3.6cqi; font-family: var(--mono); font-size: 1.85cqi;
  letter-spacing: .2em; text-transform: uppercase; color: var(--ink-3);
}

.mail { position: relative; padding-top: 19cqi; }
.letter-sheet {
  position: absolute; left: 7%; right: 7%; top: 0; background: var(--sheet); padding: 3.2cqi 4.4cqi 7cqi;
  box-shadow: 0 0 0 1px var(--rule), 0 8px 18px -14px rgba(40, 26, 10, .5);
}
.letter-sheet p { font-family: var(--display); font-size: 2.5cqi; line-height: 1.7; color: var(--ink-2); margin: 0 0 .5em; }
.letter-sheet .from { font-family: var(--mono); font-size: 1.95cqi; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-3); }
.envelope {
  position: relative; aspect-ratio: 9.5 / 4.7; background: var(--sheet); overflow: hidden;
  box-shadow: 0 1px 0 rgba(0, 0, 0, .14), 0 16px 30px -20px rgba(40, 26, 10, .6), inset 0 0 0 1px var(--rule);
}
.flap {
  position: absolute; inset: 0 0 auto 0; height: 62%; background: linear-gradient(174deg, var(--sheet), var(--manila));
  clip-path: polygon(0 0, 100% 0, 50% 100%);
}
.env-to { position: absolute; left: 33%; bottom: 7cqi; font-family: var(--mono); font-size: 2.2cqi; line-height: 1.9; color: var(--ink-2); z-index: 2; }

.frame {
  padding: 4.2cqi; background: linear-gradient(135deg, #7E6242, #5E4830 22%, #8B6E4B 50%, #5A4530 78%, #7A5F3F);
  box-shadow: 0 18px 30px -18px rgba(40, 26, 10, .6);
}
.mat { background: var(--sheet); padding: 8cqi; box-shadow: inset 0 0 0 1px rgba(26, 22, 17, .16); }
.mat img { display: block; width: 100%; height: auto; aspect-ratio: 1 / 1; object-fit: cover; box-shadow: 0 0 0 1px rgba(26, 22, 17, .25); }

.obj.bookwrap { max-width: 250px; }
.book { display: flex; filter: drop-shadow(0 20px 24px rgba(40, 26, 10, .4)); }
.book .spine { width: 7.4cqi; background: linear-gradient(90deg, #20303A, #14212A 35%, #2B3C47); display: grid; place-items: center; padding: 3cqi 0; overflow: hidden; }
.book .spine span { writing-mode: vertical-rl; transform: rotate(180deg); color: #E5DCCA; font-family: var(--display); font-size: 2.1cqi; letter-spacing: .04em; white-space: nowrap; }
.book .cover { flex: 1; aspect-ratio: 5.5 / 8.5; position: relative; overflow: hidden; background: linear-gradient(120deg, #26384A, #1B2A38 60%, #2E4053); }
.book .cover img { position: absolute; left: 0; right: 0; top: 10cqi; height: 46cqi; width: 100%; object-fit: cover; opacity: .92; }
.book .btitle { position: absolute; left: 5cqi; right: 5cqi; bottom: 12cqi; color: #F2E9D6; font-family: var(--display); font-size: 5.2cqi; line-height: 1.05; }
.book .bsub { position: absolute; left: 5cqi; right: 5cqi; bottom: 6cqi; color: #A9B7C2; font-family: var(--mono); font-size: 1.85cqi; letter-spacing: .17em; text-transform: uppercase; }
.book .btop { position: absolute; left: 5cqi; top: 5cqi; color: #8FA1AE; font-family: var(--mono); font-size: 1.75cqi; letter-spacing: .2em; text-transform: uppercase; }
.book .edge { width: 2.4cqi; background: repeating-linear-gradient(90deg, #EDE6D6 0 1px, #CFC6B2 1px 2px); }

.notecard {
  aspect-ratio: 7 / 5; background: var(--sheet); position: relative; display: grid; place-items: center;
  box-shadow: 0 1px 0 rgba(0, 0, 0, .1), 0 14px 24px -20px rgba(40, 26, 10, .5), inset 0 0 0 1px var(--rule);
}
.notecard::before {
  content: ""; position: absolute; left: 12%; right: 12%; top: 48%; height: 34%; opacity: .5;
  background: repeating-linear-gradient(to bottom, transparent 0 calc(1.9em - 1px), var(--rule) calc(1.9em - 1px) 1.9em);
}
.rstamp.shut { color: var(--red-ink); transform: rotate(-7deg); font-size: .74rem; padding: 9px 14px; position: relative; }

.cap { margin: 13px 0 0; font-size: .885rem; line-height: 1.55; color: var(--ink-2); max-width: 52ch; }
.cap b { font-weight: 400; color: var(--ink); font-family: var(--display); font-size: .98rem; }
.slip { margin: 13px 0 0; display: grid; grid-template-columns: auto 1fr; border-top: 1px solid var(--rule); }
.slip > div { display: contents; }
.slip dt {
  font-family: var(--mono); font-size: .62rem; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-3);
  padding: 9px 14px 7px 0; border-bottom: 1px solid var(--rule);
}
.slip dd { margin: 0; padding: 7px 0; border-bottom: 1px solid var(--rule); font-size: .85rem; color: var(--ink-2); }
.slip dd b { font-weight: 600; color: var(--ink); }

.grid3 { display: grid; gap: clamp(28px, 3.4vw, 44px) clamp(22px, 3vw, 36px); }
@media (min-width: 660px) { .grid3 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1020px) { .grid3 { grid-template-columns: repeat(3, minmax(0, 1fr)); } }

.drawn { margin-top: 26px; display: flex; flex-wrap: wrap; gap: 16px 26px; align-items: center; }
.drawn p { flex: 1 1 300px; margin: 0; }
.order { height: 100%; display: flex; flex-direction: column; box-sizing: border-box; }
.order-head { margin: 12px 0 14px; }
.push { margin-top: auto; padding-top: 16px; }
.got { margin-top: 24px; }
.got p, .tail { margin-top: 10px; }

.tariff { width: 100%; border-collapse: collapse; font-size: .88rem; margin-top: 12px; }
.tariff th, .tariff td { text-align: left; padding: 9px 10px 9px 0; border-bottom: 1px solid var(--rule); vertical-align: top; }
.tariff th { font-family: var(--mono); font-size: .62rem; letter-spacing: .15em; text-transform: uppercase; color: var(--ink-3); font-weight: 500; }
.tariff td:last-child { text-align: right; padding-right: 0; white-space: nowrap; }

.trade-head { margin: 12px 0 14px; max-width: 20ch; }
/* .riso .panel resets border; restated at higher specificity */
.panel.airmail.closer {
  border: 8px solid transparent;
  border-image: repeating-linear-gradient(45deg, var(--red) 0 9px, var(--sheet) 9px 18px, var(--blue) 18px 27px, var(--sheet) 27px 36px) 8;
}
.closer { padding: clamp(24px, 4vw, 44px); text-align: center; background: var(--sheet); }
.closer .d2 { max-width: 20ch; margin: 0 auto; }
.closer .lede { margin: 12px auto 0; max-width: 46ch; }
.colophon { margin-top: 22px; max-width: 72ch; color: var(--ink-3); }
</style>
